import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { Permission } from '../../types/permissions';
import { Layout } from '../../components/Layout';
import { PageTitle } from '../../components/PageTitle';
import { Loading } from '../../components/Loading';
import { Input } from '../../components/Input';

interface ProfileFormData {
  name: string;
  description: string;
  is_active: boolean;
}

export function ProfileForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!id;

  useEffect(() => {
    loadPermissions();
    if (id) {
      loadProfile();
    }
  }, [id]);

  async function loadPermissions() {
    try {
      setLoadingPermissions(true);
      const response = await api.get('/permissions');
      setPermissions(response.data);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar permissões',
        { variant: 'error' }
      );
    } finally {
      setLoadingPermissions(false);
    }
  }

  async function loadProfile() {
    try {
      setLoading(true);
      const [profileResponse, permissionsResponse] = await Promise.all([
        api.get(`/profiles/${id}`),
        api.get(`/profiles/${id}/permissions`),
      ]);

      const profile = profileResponse.data;
      setName(profile.name);
      setDescription(profile.description);
      setIsActive(profile.is_active);

      const profilePermissions = permissionsResponse.data || [];
      setSelectedPermissions(profilePermissions.map((p: Permission) => p.id));
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar perfil',
        { variant: 'error' }
      );
      navigate('/profiles');
    } finally {
      setLoading(false);
    }
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const data = {
        name: name.trim(),
        description: description.trim(),
        is_active: isActive,
      };

      if (isEditMode) {
        await api.put(`/profiles/${id}`, data);
        await api.put(`/profiles/${id}/permissions`, {
          permission_ids: selectedPermissions,
        });
        enqueueSnackbar('Perfil atualizado com sucesso!', { variant: 'success' });
      } else {
        const response = await api.post('/profiles', data);
        const profileId = response.data.id;

        if (selectedPermissions.length > 0) {
          await api.put(`/profiles/${profileId}/permissions`, {
            permission_ids: selectedPermissions,
          });
        }

        enqueueSnackbar('Perfil criado com sucesso!', { variant: 'success' });
      }

      navigate('/profiles');
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || `Erro ao ${isEditMode ? 'atualizar' : 'criar'} perfil`,
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  }

  function togglePermission(permissionId: string) {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }

  function toggleAllPermissions() {
    if (selectedPermissions.length === permissions.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(permissions.map(p => p.id));
    }
  }

  // Tradução dos recursos para português
  const resourceTranslations: Record<string, string> = {
    'users': 'Usuários',
    'costumers': 'Clientes',
    'costumer-observations': 'Observações de Clientes',
    'services': 'Serviços',
    'equipments': 'Equipamentos',
    'sectors': 'Setores',
    'tech-visit-reports': 'Relatórios de Visita Técnica',
    'zones': 'Zonas',
    'responsibles': 'Responsáveis',
    'dashboard': 'Dashboard',
    'costumers-monitoring': 'Monitoramento de Clientes',
    'costumers-map': 'Mapa de Clientes',
    'profiles': 'Perfis de Acesso',
    'permissions': 'Permissões',
  };

  function translateResource(resource: string): string {
    return resourceTranslations[resource] || resource.replace(/-/g, ' ');
  }

  // Agrupar permissões por recurso
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageTitle
          title={isEditMode ? 'Editar Perfil' : 'Novo Perfil'}
          backButton
          backPath="/profiles"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Informações Básicas</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Perfil
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Gerente, Técnico, Supervisor"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0085BD] focus:border-transparent"
              />
              {errors.name && (
                <span className="text-sm text-red-600">{errors.name}</span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0085BD] focus:border-transparent"
                placeholder="Descreva as responsabilidades deste perfil"
              />
              {errors.description && (
                <span className="text-sm text-red-600">{errors.description}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-[#0085BD] border-gray-300 rounded focus:ring-[#0085BD]"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Perfil ativo
              </label>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Permissões</h3>
              <button
                type="button"
                onClick={toggleAllPermissions}
                className="text-sm text-[#0085BD] hover:text-[#006a96] font-medium"
              >
                {selectedPermissions.length === permissions.length
                  ? 'Desmarcar Todas'
                  : 'Selecionar Todas'}
              </button>
            </div>

            {loadingPermissions ? (
              <div className="text-center py-8">
                <Loading />
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <div key={resource} className="border-l-4 border-[#0085BD] pl-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {translateResource(resource)}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {perms.map(permission => (
                        <label
                          key={permission.id}
                          className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => togglePermission(permission.id)}
                            className="mt-1 w-4 h-4 text-[#0085BD] border-gray-300 rounded focus:ring-[#0085BD]"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {permission.action}
                            </div>
                            <div className="text-xs text-gray-500">
                              {permission.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => navigate('/profiles')}
              className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-[#0085BD] hover:bg-[#006a96] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? 'Salvando...'
                : isEditMode
                ? 'Salvar Alterações'
                : 'Criar Perfil'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
